import React, { useState, useEffect } from "react";
import { ChevronDown, Calendar, Loader2 } from "lucide-react";
import { TrendUp, SearchNormal } from "iconsax-reactjs";
import AskGenziButton from "./AskGenziButton";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Area,
  AreaChart,
} from "recharts";
import api from "../../services/api";

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-[#1A1D23] border border-gray-700 rounded-lg p-3 shadow-xl">
        <p className="text-white font-medium mb-2">{label}</p>
        {payload.map((entry, index) => (
          <p key={index} className="text-sm" style={{ color: entry.color }}>
            {entry.name.charAt(0).toUpperCase() + entry.name.slice(1)}:{" "}
            {entry.value}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

const Insights = () => {
  const [selectedFilters, setSelectedFilters] = useState({
    speaker: "Speaker",
    tag: "Tag",
  });

  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  const [dateRange, setDateRange] = useState({
    startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
    endDate: new Date().toISOString(),
  });

  const [stats, setStats] = useState({
    totalCallsAnalysed: 0,
    averageDuration: "00:00:00",
    totalQualified: 0,
    averageCustomerSentiment: "Neutral",
  });

  const [durationData, setDurationData] = useState([]);
  const [sentimentTrendData, setSentimentTrendData] = useState([]);
  const [qualificationTrendData, setQualificationTrendData] = useState([]);
  const [teamData, setTeamData] = useState([]);

  useEffect(() => {
    fetchAnalyticsData();
  }, [dateRange]);

  const fetchAnalyticsData = async () => {
    try {
      setLoading(true);

      const response = await api.getCallAnalyticsByAgents({
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
      });

      if (response.success && response.analytics) {
        setAnalytics(response.analytics);
        processAnalyticsData(response.analytics);
      }
    } catch (error) {
      console.error("Error fetching analytics:", error);
    } finally {
      setLoading(false);
    }
  };

  const processAnalyticsData = (data) => {
    const { overview, sentimentBreakdown, agentPerformance } = data;

    // Calculate total qualified calls
    const totalQualified = agentPerformance.reduce(
      (sum, agent) => sum + (agent.qualifiedCalls || 0),
      0,
    );

    // Update stats
    setStats({
      totalCallsAnalysed: overview?.totalCalls || 0,
      averageDuration: formatDuration(overview?.averageDuration || 0),
      totalQualified: totalQualified,
      averageCustomerSentiment: calculateAverageSentiment(sentimentBreakdown),
    });

    // Process team data from agent performance
    if (agentPerformance && agentPerformance.length > 0) {
      const teamTableData = agentPerformance.map((agent) => ({
        id: agent.agentEmail,
        email: agent.agentEmail,
        avatar: `https://ui-avatars.com/api/?name=${encodeURIComponent(
          agent.agentEmail,
        )}&background=7c3aed&color=fff`,
        callCount: agent.totalCalls,
        avgDuration: agent.avgDuration, // Keep in seconds for display
        avgSentiment: calculateSentimentScore(agent.sentimentBreakdown),
        qualificationRate: agent.qualificationRate,
        avgLeadScore: agent.avgLeadScore,
        appointmentsBooked: agent.appointmentsBooked || 0,
        qualifiedCalls: agent.qualifiedCalls || 0,
      }));
      setTeamData(teamTableData);

      // Create duration chart from agent performance (top 10 agents)
      const durationChartData = agentPerformance
        .sort((a, b) => b.totalCalls - a.totalCalls)
        .slice(0, 10)
        .map((agent) => ({
          name: agent.agentEmail.split("@")[0] || agent.agentEmail,
          duration: Math.round(agent.avgDuration),
          calls: agent.totalCalls,
        }));
      setDurationData(durationChartData);

      // Create sentiment trend data from agents
      const sentimentData = agentPerformance
        .filter((agent) => agent.totalCalls > 0)
        .map((agent) => ({
          name: agent.agentEmail.split("@")[0] || agent.agentEmail,
          positive: agent.sentimentBreakdown?.positive || 0,
          neutral: agent.sentimentBreakdown?.neutral || 0,
          negative: agent.sentimentBreakdown?.negative || 0,
        }));
      setSentimentTrendData(sentimentData);

      // Create qualification trend data
      const qualificationData = agentPerformance
        .filter((agent) => agent.totalCalls > 0)
        .map((agent) => ({
          name: agent.agentEmail.split("@")[0] || agent.agentEmail,
          qualified: agent.qualifiedCalls || 0,
          notQualified: agent.totalCalls - (agent.qualifiedCalls || 0),
          appointments: agent.appointmentsBooked || 0,
        }));
      setQualificationTrendData(qualificationData);
    }
  };

  const calculateSentimentScore = (sentimentBreakdown) => {
    if (!sentimentBreakdown) return 3;

    const total =
      (sentimentBreakdown.positive || 0) +
      (sentimentBreakdown.neutral || 0) +
      (sentimentBreakdown.negative || 0);

    if (total === 0) return 3;

    const positiveWeight = 5;
    const neutralWeight = 3;
    const negativeWeight = 1;

    const score =
      ((sentimentBreakdown.positive || 0) * positiveWeight +
        (sentimentBreakdown.neutral || 0) * neutralWeight +
        (sentimentBreakdown.negative || 0) * negativeWeight) /
      total;

    return Number(score.toFixed(1));
  };

  const calculateAverageSentiment = (sentimentBreakdown) => {
    if (!sentimentBreakdown) return "Neutral";

    const total =
      (sentimentBreakdown.positive || 0) +
      (sentimentBreakdown.neutral || 0) +
      (sentimentBreakdown.negative || 0);

    if (total === 0) return "Neutral";

    const positiveRatio = (sentimentBreakdown.positive || 0) / total;
    const negativeRatio = (sentimentBreakdown.negative || 0) / total;

    if (positiveRatio > 0.5) return "Positive";
    if (negativeRatio > 0.5) return "Negative";
    return "Neutral";
  };

  const formatDuration = (seconds) => {
    if (!seconds || seconds === 0) return "00:00:00";
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(
      2,
      "0",
    )}:${String(secs).padStart(2, "0")}`;
  };

  const StatCard = ({ icon, title, value, change }) => (
    <div className="bg-[#0C0E12] rounded-xl p-6 border border-gray-700/50 hover:border-gray-600/50 transition-colors">
      <div className="flex items-center justify-between mb-4">
        <div className="bg-[#7c3aed] p-3 rounded-xl">{icon}</div>
        <div className="flex items-center justify-between">
          <button className="text-gray-400 hover:text-gray-300 p-1">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
              <circle cx="8" cy="3" r="1.5" />
              <circle cx="8" cy="8" r="1.5" />
              <circle cx="8" cy="13" r="1.5" />
            </svg>
          </button>
        </div>
      </div>
      <div className="space-y-2">
        <div className="text-[#94979C] text-[14px] xl:text-[12px] font-medium">
          {title}
        </div>
        <div className="flex items-end justify-between">
          <div className="text-[#F7F7F7] text-[28px] xl:text-[20px] font-bold">
            {value}
          </div>
          {change && (
            <div className="flex items-center text-white text-sm xl:text-[10px] font-medium">
              <TrendUp className="mr-1 text-green-400 xl:w-5 xl:h-5" />
              {change}%
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const CallRow = ({ agent }) => (
    <tr className="border-b border-gray-700 hover:bg-gray-800/50 transition-colors">
      <td className="py-4 px-4">
        <div className="flex items-center gap-3">
          <img
            src={agent.avatar}
            alt={agent.email}
            className="w-10 h-10 rounded-full object-cover"
          />
          <div>
            <div className="text-white font-medium">{agent.email}</div>
          </div>
        </div>
      </td>
      <td className="py-4 px-4 text-gray-400">{agent.callCount}</td>
      <td className="py-4 px-4">
        <span className="text-white font-medium">
          {formatDuration(agent.avgDuration)}
        </span>
      </td>
      <td className="py-4 px-4">
        <div className="flex items-center gap-2">
          <div className="w-16 h-2 bg-gray-700 rounded-full overflow-hidden">
            <div
              className="h-full bg-purple-500"
              style={{ width: `${agent.avgLeadScore}%` }}
            ></div>
          </div>
          <span className="text-gray-400 text-sm">
            {agent.avgLeadScore}/100
          </span>
        </div>
      </td>
      <td className="py-4 px-4">
        <span className="text-white font-medium">
          {agent.appointmentsBooked}
        </span>
      </td>
    </tr>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0C0E12] text-white flex items-center justify-center">
        <Loader2 className="animate-spin text-purple-500" size={60} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0C0E12] text-white font-inter">
      <div className="p-4 lg:p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 sm:gap-6 mb-6 sm:mb-8">
          <div className="flex-1 min-w-0">
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-1 sm:mb-2 text-white">
              Insights
            </h1>
            <p className="text-sm sm:text-base text-gray-400 leading-relaxed">
              Analyze trends and patterns in your call data.
            </p>
          </div>
        </div>
      

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <StatCard
            icon={<SearchNormal />}
            title="Total calls analysed"
            value={stats.totalCallsAnalysed}
            change={null}
          />
          <StatCard
            icon={<SearchNormal />}
            title="Average duration"
            value={stats.averageDuration}
            change={null}
          />
          <StatCard
            icon={<SearchNormal />}
            title="Qualified calls"
            value={stats.totalQualified}
            change={null}
          />
          <StatCard
            icon={<SearchNormal />}
            title="Average customer sentiment"
            value={stats.averageCustomerSentiment}
            change={null}
          />
        </div>

        {/* Charts */}
        <div className="grid grid-cols-1 gap-6 mx-auto">
          {/* Average Duration by Agent */}
          {durationData.length > 0 && (
            <div className="bg-[#0C0E12] border-[#373A41] border rounded-lg p-4 lg:col-span-2">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-base font-medium">
                  Average Call Duration by Agent
                </h2>
                <button className="text-gray-400 hover:text-white">⋮</button>
              </div>
              <p className="text-xs text-gray-400 mb-4">
                Top {durationData.length} agents by call volume
              </p>
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart
                  data={durationData}
                  margin={{ top: 5, right: 5, left: 5, bottom: 5 }}
                >
                  <defs>
                    <linearGradient
                      id="durationGradient"
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop offset="5%" stopColor="#8B5CF6" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#8B5CF6" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="#374151"
                    vertical={false}
                  />
                  <XAxis
                    dataKey="name"
                    stroke="#9CA3AF"
                    fontSize={12}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    stroke="#9CA3AF"
                    fontSize={12}
                    axisLine={false}
                    tickLine={false}
                    label={{
                      value: "Seconds",
                      angle: -90,
                      position: "insideLeft",
                      style: { fill: "#9CA3AF", fontSize: 12 },
                    }}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Area
                    type="monotone"
                    dataKey="duration"
                    stroke="#8B5CF6"
                    strokeWidth={3}
                    fill="url(#durationGradient)"
                    dot={{ fill: "#8B5CF6", strokeWidth: 2, r: 4 }}
                    activeDot={{ r: 6, fill: "#8B5CF6" }}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Qualification Performance */}
            {qualificationTrendData.length > 0 && (
              <div className="bg-[#0C0E12] border-[#373A41] border rounded-lg p-4 h-full">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-base font-medium">
                    Qualification Performance
                  </h2>
                  <button className="text-gray-400 hover:text-white">⋮</button>
                </div>
                <p className="text-xs text-gray-400 mb-2">
                  Qualified vs Not Qualified by Agent
                </p>
                <div className="flex gap-4 text-xs mb-4">
                  <span className="flex items-center gap-1">
                    <span className="w-3 h-3 bg-purple-600 rounded-full"></span>
                    Qualified
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="w-3 h-3 bg-purple-400 rounded-full"></span>
                    Not Qualified
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="w-3 h-3 bg-green-500 rounded-full"></span>
                    Appointments
                  </span>
                </div>
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart
                    data={qualificationTrendData}
                    margin={{ top: 5, right: 5, left: 5, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                    <XAxis
                      dataKey="name"
                      tick={{ fill: "#9CA3AF", fontSize: 10 }}
                      axisLine={{ stroke: "#374151" }}
                    />
                    <YAxis
                      tick={{ fill: "#9CA3AF", fontSize: 10 }}
                      axisLine={{ stroke: "#374151" }}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar
                      dataKey="qualified"
                      stackId="a"
                      fill="#7c3aed"
                      radius={[0, 0, 0, 0]}
                    />
                    <Bar
                      dataKey="notQualified"
                      stackId="a"
                      fill="#a78bfa"
                      radius={[0, 0, 0, 0]}
                    />
                    <Bar
                      dataKey="appointments"
                      fill="#10b981"
                      radius={[8, 8, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* Sentiment Breakdown */}
            {sentimentTrendData.length > 0 && (
              <div className="bg-[#0C0E12] border-[#373A41] border rounded-lg p-4 h-full">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-base font-medium">
                    Sentiment Distribution
                  </h2>
                  <button className="text-gray-400 hover:text-white">⋮</button>
                </div>
                <p className="text-xs text-gray-400 mb-2">
                  Sentiment breakdown by agent
                </p>
                <div className="flex gap-4 text-xs mb-4">
                  <span className="flex items-center gap-1">
                    <span className="w-3 h-3 bg-green-500 rounded-full"></span>
                    Positive
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="w-3 h-3 bg-yellow-500 rounded-full"></span>
                    Neutral
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="w-3 h-3 bg-red-500 rounded-full"></span>
                    Negative
                  </span>
                </div>
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart
                    data={sentimentTrendData}
                    margin={{ top: 5, right: 5, left: 5, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                    <XAxis
                      dataKey="name"
                      tick={{ fill: "#9CA3AF", fontSize: 10 }}
                      axisLine={{ stroke: "#374151" }}
                    />
                    <YAxis
                      tick={{ fill: "#9CA3AF", fontSize: 10 }}
                      axisLine={{ stroke: "#374151" }}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar
                      dataKey="positive"
                      stackId="a"
                      fill="#10b981"
                      radius={[0, 0, 0, 0]}
                    />
                    <Bar
                      dataKey="neutral"
                      stackId="a"
                      fill="#eab308"
                      radius={[0, 0, 0, 0]}
                    />
                    <Bar
                      dataKey="negative"
                      stackId="a"
                      fill="#ef4444"
                      radius={[8, 8, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        </div>

        {/* Team Management Table */}
        <div className="bg-[#0C0E12] rounded-lg border border-gray-700 mt-8">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between p-6 border-b border-gray-700 gap-4">
            <div>
              <h2 className="text-xl font-bold text-white">Team Performance</h2>
              <p className="text-gray-400 text-sm mt-1">
                Agent performance metrics and statistics.
              </p>
            </div>
          </div>

          <div className="overflow-x-auto">
            {teamData.length === 0 ? (
              <div className="text-center py-20 text-gray-400">
                No team data available
              </div>
            ) : (
              <table className="w-full min-w-[900px]">
                <thead className="bg-[#13161B]">
                  <tr className="border-b border-[#22262F]">
                    <th className="text-left py-4 px-4 text-gray-400 font-medium text-sm">
                      Agent Email
                    </th>
                    <th className="text-left py-4 px-4 text-gray-400 font-medium text-sm">
                      Total Calls
                    </th>
                    <th className="text-left py-4 px-4 text-gray-400 font-medium text-sm">
                      Avg Duration
                    </th>
                    <th className="text-left py-4 px-4 text-gray-400 font-medium text-sm">
                      Avg Lead Score
                    </th>
                    <th className="text-left py-4 px-4 text-gray-400 font-medium text-sm">
                      Appointments Booked
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {teamData.map((agent) => (
                    <CallRow key={agent.id} agent={agent} />
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center justify-between p-6 border-t border-gray-700 gap-4">
            <div className="flex gap-2">
              <button className="px-3 py-1 bg-[#0C0E12] border-[#373A41] border text-gray-300 rounded hover:bg-gray-600 transition-colors">
                Previous
              </button>
              <button className="px-3 py-1 bg-[#0C0E12] border-[#373A41] border text-gray-300 rounded hover:bg-gray-600 transition-colors">
                Next
              </button>
            </div>
            <span className="text-gray-400 text-sm">
              Showing {teamData.length} agents
            </span>
          </div>
        </div>

        <AskGenziButton />
      </div>
    </div>
  );
};

export default Insights;
